import appConfig from "config.json";
import { isCurrentPathPresent, toggleBodyClass } from "utils";
import { defaultThemeVariant } from "config-txp";
import { getParam, validateBodyClasses } from "./paramsValidation";

const navFloatConfig = {
	checkoutExpressPage: ["checkoutConfirmationPage"],
	"help-and-faq-background": ["hyperion"],
};

const navFloatMultiClassConfig = [["cart", "STORE_VWCNFG_STYLES_WDW", "pepde"]];
const navFloatClass = "nav-float";
const navGradientClass = "nav-gradient";

export const isNavFloat = () => validateBodyClasses(navFloatClass);
export const isNavGradient = () => validateBodyClasses(navGradientClass);

let navFloatAppliedByUs = false;
let navGradientAppliedByUs = false;

const getTxpConfig = (brand, environment) => {
	const envConfig =
		appConfig.environmentConfiguration?.[environment]?.txp?.[brand] || {};
	const brandConfig = appConfig.brands?.[brand]?.txp || {};
	return { ...brandConfig, ...envConfig };
};

const isPageInTxpList = (brand, environment, key, updatedLocation) => {
	const txpConfig = getTxpConfig(brand, environment);
	const pages = txpConfig?.[key] || [];
	return pages.length > 0 && isCurrentPathPresent(pages, updatedLocation);
};

export const applyNavFloatClass = (brand, environment, updatedLocation) => {
	const shouldApplyByBodyClass = Object.entries(navFloatConfig).some(
		([requiredClass, excludedClasses]) =>
			validateBodyClasses(requiredClass) &&
			!excludedClasses.some(validateBodyClasses)
	);

	const shouldApplyByMultiClass = navFloatMultiClassConfig.some((classGroup) =>
		classGroup.every(validateBodyClasses)
	);

	const shouldApplyByPage = isPageInTxpList(
		brand,
		environment,
		"navFloatPages",
		updatedLocation
	);
	const shouldApply =
		shouldApplyByBodyClass || shouldApplyByMultiClass || shouldApplyByPage;

	if (shouldApply && !navFloatAppliedByUs) {
		toggleBodyClass(true, navFloatClass);
		navFloatAppliedByUs = true;
	} else if (!shouldApply && navFloatAppliedByUs) {
		toggleBodyClass(false, navFloatClass);
		navFloatAppliedByUs = false;
	}
};

const availableThemes = { light: "nav-light", dark: "nav-dark" };
// Function to get theme key from theme value
export const getThemeKey = (themeValue) => {
	return Object.keys(availableThemes).find(
		(key) => availableThemes[key] === themeValue
	);
};

export const shouldInvertItems = (isTxp) =>
	getThemeKey(isTxp) !== defaultThemeVariant;

const getTxpConfigTheme = (brand, locale, environment, updatedLocation) => {
	const txpConfig = getTxpConfig(brand, environment);

	if (
		!txpConfig?.enabled ||
		(txpConfig.allowedLocales && !txpConfig.allowedLocales.includes(locale))
	) {
		return null;
	}

	return !isCurrentPathPresent(txpConfig.darkThemePages || {}, updatedLocation)
		? availableThemes.light
		: availableThemes.dark;
};

export const applyNavGradientClass = (brand, environment, updatedLocation) => {
	const shouldApply = isPageInTxpList(
		brand,
		environment,
		"navGradientPages",
		updatedLocation
	);

	if (shouldApply && !navGradientAppliedByUs) {
		toggleBodyClass(true, navGradientClass);
		navGradientAppliedByUs = true;
	} else if (!shouldApply && navGradientAppliedByUs) {
		toggleBodyClass(false, navGradientClass);
		navGradientAppliedByUs = false;
	}
};

export const getTXPtheme = (brand, locale, environment, updatedLocation) => {
	if (!appConfig?.txpAllowedBrands?.includes(brand)) return false;
	const theme =
		window?.localStorage.getItem("data-nav-theme") ||
		new URLSearchParams(window.location.search).get("dataNavTheme") ||
		getTxpConfigTheme(brand, locale, environment, updatedLocation);

	if (!theme || !Object.values(availableThemes).includes(theme)) return false;

	// We should not replace the current assigned theme here
	if (!document.body.dataset.navTheme) {
		document.body.dataset.navTheme = theme;
	}

	applyNavGradientClass(brand, environment, updatedLocation);
	applyNavFloatClass(brand, environment, updatedLocation);

	return theme;
};

export const recalculateTxpClasses = (
	brand,
	locale,
	environment,
	updatedLocation
) => {
	if (!appConfig?.txpAllowedBrands?.includes(brand)) return;

	const theme = getTxpConfigTheme(brand, locale, environment, updatedLocation);
	if (theme && Object.values(availableThemes).includes(theme)) {
		document.body.dataset.navTheme = theme;
	}

	applyNavGradientClass(brand, environment, updatedLocation);
	applyNavFloatClass(brand, environment, updatedLocation);
};

export const getSearchAutocompleteEnginte = () => {
	const searchEngineParam = getParam("searchEngine");

	const searchEngineLocalStorage = window?.localStorage.getItem("searchEngine");

	return searchEngineParam || searchEngineLocalStorage;
};

const navInertKeepSelectors = ".syndicated-header-parent, #syndicated-root";

// Finds the nav container element (the one that holds the header + overlay).
// The consumer-provided id varies per site (syndicatedHeader, headerWrapper,
// ...), so we match on the stable `.syndicated-header-parent` class (falling
// back to #syndicated-root). Returns the first match or null.
export const getNavContainer = (keepSelectors = navInertKeepSelectors) => {
	return document?.querySelector(keepSelectors) || null;
};

// Marks the host page's background as inert + aria-hidden so screen readers
// cannot reach it while the mobile menu is open. Because the nav container may
// be nested at any depth (e.g. inside #pageContainer on the cart page), we walk
// UP from the nav container and, at EACH ancestor level, inert the siblings of
// the current node until we reach <body>. This isolates the nav regardless of
// the host page structure. Only attributes this helper adds are tracked (via
// data-nav-* markers) so pre-existing attributes are never clobbered. Returns
// the list of elements it touched.
export const applyBackgroundInert = (keepSelectors = navInertKeepSelectors) => {
	const inertedElements = [];
	try {
		const navContainer = getNavContainer(keepSelectors);
		if (!navContainer) {
			return inertedElements;
		}
		let node = navContainer;
		while (node && node !== document.body && node.parentNode) {
			const currentNode = node;
			[...currentNode.parentNode.children].forEach((sibling) => {
				if (sibling === currentNode || !sibling.isConnected) {
					return;
				}
				if (!sibling.hasAttribute("inert")) {
					sibling.setAttribute("inert", "");
					sibling.setAttribute("data-nav-inerted", "true");
				}
				if (!sibling.hasAttribute("aria-hidden")) {
					sibling.setAttribute("aria-hidden", "true");
					sibling.setAttribute("data-nav-aria-hidden", "true");
				}
				inertedElements.push(sibling);
			});
			node = currentNode.parentNode;
		}
	} catch (error) {
		// Non-critical a11y enhancement: never let a DOM access failure break
		// the navigation. Background stays reachable in the worst case.
	}
	return inertedElements;
};

// Reverts inert + aria-hidden ONLY on the elements this helper previously
// marked (data-nav-* markers), leaving any pre-existing attributes intact.
export const removeBackgroundInert = (inertedElements = []) => {
	try {
		inertedElements.forEach((element) => {
			if (!element?.isConnected) {
				return;
			}
			if (element.getAttribute("data-nav-inerted") === "true") {
				element.removeAttribute("inert");
				element.removeAttribute("data-nav-inerted");
			}
			if (element.getAttribute("data-nav-aria-hidden") === "true") {
				element.removeAttribute("aria-hidden");
				element.removeAttribute("data-nav-aria-hidden");
			}
		});
	} catch (error) {
		// Non-critical: ignore cleanup errors so the nav is never affected.
	}
};
